import React, { useState } from 'react';
import { useApi, messages as messagesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

function ChatBubble({ msg, isMine }) {
  return (
    <div style={{ display: 'flex', justifyContent: isMine ? 'flex-end' : 'flex-start', marginBottom: '0.6rem' }}>
      <div
        style={{
          maxWidth: '75%', padding: '0.6rem 0.85rem', borderRadius: 'var(--radius-md)',
          background: isMine ? 'var(--brand)' : 'var(--surface-alt)',
          color: isMine ? 'var(--surface)' : 'var(--ink)',
          fontSize: 'var(--text-sm)', lineHeight: 1.5,
        }}
      >
        <div>{msg.body}</div>
        <div style={{ fontSize: 'var(--text-xs)', opacity: 0.7, marginTop: '0.25rem', textAlign: 'right' }}>
          {new Date(msg.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
        </div>
      </div>
    </div>
  );
}

function ThreadView({ threadType, threadId, onBack }) {
  const { user } = useAuth();
  const path = threadType === 'for_sale' ? `/messages/for-sale/${threadId}` : `/messages/${threadId}`;
  const { data, loading, refetch } = useApi(path);
  const msgs = data?.messages || [];
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  async function send(e) {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      if (threadType === 'for_sale') {
        await messagesApi.sendForSale(threadId, text.trim());
      } else {
        await messagesApi.send(threadId, text.trim());
      }
      setText('');
      refetch();
    } finally {
      setSending(false);
    }
  }

  return (
    <div>
      <button type="button" onClick={onBack} className="btn btn-outline btn-sm" style={{ marginBottom: '1rem' }}>
        ← Back to conversations
      </button>

      {loading ? (
        <p style={{ color: 'var(--ink-muted)' }}>Loading…</p>
      ) : (
        <div style={{ maxHeight: 420, overflowY: 'auto', padding: '1rem', background: 'var(--surface-alt)', borderRadius: 'var(--radius-md)', marginBottom: '1rem' }}>
          {msgs.length === 0 ? (
            <p style={{ color: 'var(--ink-faint)' }}>No messages yet — say hello!</p>
          ) : (
            msgs.map(m => <ChatBubble key={m.id} msg={m} isMine={m.sender_id === user.id} />)
          )}
        </div>
      )}

      <form onSubmit={send} style={{ display: 'flex', gap: '0.5rem' }}>
        <input
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Type a message…"
          style={{ flex: 1 }}
        />
        <button type="submit" className="btn btn-primary" disabled={sending || !text.trim()}>
          {sending ? 'Sending…' : 'Send'}
        </button>
      </form>
    </div>
  );
}

// Conversations list, grouped by thread (rental listing or for-sale
// listing), with a chat-bubble thread view. Shared by both the buyer and
// owner dashboards' Messages tabs.
export default function MessagesPanel({ initialListingId = null }) {
  const { data, loading, refetch } = useApi('/messages');
  const conversations = data?.conversations || [];
  const [activeThread, setActiveThread] = useState(
    initialListingId ? { type: 'listing', id: initialListingId } : null
  );

  if (activeThread) {
    return (
      <ThreadView
        threadType={activeThread.type}
        threadId={activeThread.id}
        onBack={() => { setActiveThread(null); refetch(); }}
      />
    );
  }

  if (loading) return <p style={{ color: 'var(--ink-muted)' }}>Loading conversations…</p>;
  if (conversations.length === 0) return <p style={{ color: 'var(--ink-muted)' }}>No messages yet.</p>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {conversations.map(c => (
        <div
          key={`${c.thread_type}-${c.thread_id}`}
          className="card"
          style={{ padding: '1rem', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem' }}
          onClick={() => setActiveThread({ type: c.thread_type, id: c.thread_id })}
        >
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 600 }}>
              {c.property_name}{c.unit_number ? ` · ${c.unit_number}` : ''}
              {c.thread_type === 'for_sale' && (
                <span style={{ marginLeft: '0.5rem', fontSize: 'var(--text-xs)', fontWeight: 700, color: 'var(--brand-dark)', background: 'var(--surface-alt)', borderRadius: '999px', padding: '0.1rem 0.5rem' }}>
                  For Sale
                </span>
              )}
            </div>
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {c.other_user_first_name} {c.other_user_last_name}: {c.last_message}
            </div>
          </div>
          {c.unread_count > 0 && (
            <span style={{ background: 'var(--danger)', color: 'var(--surface)', borderRadius: '999px', fontSize: 'var(--text-xs)', padding: '0.15rem 0.5rem', fontWeight: 700, flexShrink: 0 }}>
              {c.unread_count}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
